import { forwardRef, MouseEvent } from 'react'
import { ComponentProps } from '@stitches/react'
import { CommentBox } from 'common/CommentBox'
import { useTimeDistance } from 'hooks/useTimeDistance'
import { observer } from 'mobx-react-lite'
import { Post } from 'models/Post'
import { Box } from 'primitives/Box'
import { Flex } from 'primitives/Flex'
import { SnackBar } from 'common/SnackBar'
import snackBar from 'common/SnackBar/config'
import { toast } from 'components/common/Toast'
import { Comment } from 'graphql/types'
import { useStore } from 'lib/store'
import { LoadMoreButton } from 'common/LoadMoreButton'
import { NSFW_EVENT } from 'lib/tracking/types'
import { FeedEnd } from 'shared/FeedItem/FeedEnd'
import { Spinner } from 'common/Spinner'

export interface PostCommentsProps extends ComponentProps<typeof Box> {
  post: Post
}

const PostComment: React.FC<{
  comment: Comment
  blurredText?: boolean
  post: Post
}> = ({ post, comment, blurredText }) => {
  const store = useStore()
  const { timeDistance } = useTimeDistance(comment.createdAt)

  const handleAuthorClick = () => {
    store.analytics.track(NSFW_EVENT.FEED_POST_COMMENT_PROFILE_VIEW, {
      feedType: post.feedType,
      page: post.feedPage,
      postId: post.postId,
      profileId: comment.author.profileId
    })
  }

  return (
    <CommentBox
      comment={comment}
      timeDistance={timeDistance}
      blurredText={blurredText}
      onClickAuthor={handleAuthorClick}
    />
  )
}

const _PostComments = forwardRef<HTMLDivElement, PostCommentsProps>(
  ({ post, css, ...props }, ref) => {
    const store = useStore()

    const hasPosts = post.comments.items.length > 0
    const isLoading = post.comments.loading

    const fetchComments = async (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (store.ui.handleGatedAction()) return

      const button = e.target as HTMLButtonElement
      button.disabled = true
      try {
        await post.fetchComments()
      } catch (err) {
        // toast(() => <SnackBar title={`Unable to load more comments`} />, snackBar)
      }
      button.disabled = false
    }

    return (
      <Box
        css={{
          width: '100%',
          padding: '0px 16px',
          '@md': { padding: '0px' },
          ...css
        }}
        ref={ref}
        {...props}
      >
        {!hasPosts && !isLoading && <FeedEnd text="No comments" />}
        {!hasPosts && isLoading && (
          <Flex center>
            <Spinner />
          </Flex>
        )}
        <Flex column>
          {post.comments.items.map((comment) => (
            <PostComment post={post} comment={comment} key={comment.commentId} />
          ))}
        </Flex>
        {hasPosts && post.comments.hasNextPage && (
          <Flex column center css={{ height: '40px', marginTop: '20px' }}>
            <LoadMoreButton loading={isLoading} onClick={fetchComments} />
          </Flex>
        )}
      </Box>
    )
  }
)

_PostComments.displayName = '_PostComments'

export const PostComments = observer(_PostComments)
